import React from 'react';
import { alpha, Box } from '@mui/material';
import { HtmlTooltip } from 'components/html-tooltip';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { ReportWhy } from 'app/concierge/types';

interface IProps {
    why?: ReportWhy | null;
    elementId: number;
}

/**
 * The info icon beside a report an answer names (Marius, 2026-09-09): its tooltip says only why
 * the report was included. Muted while the summary is still preparing, plain once the sentence
 * has landed, absent when the row carries no why (an older payload, the switch at N, a timeout).
 * D58: the same tooltip, icon box and pointer cursor as the tile info icon beside it, never the
 * help cursor.
 */
export default function ReportWhyTooltip({ why, elementId }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    if (!why) {
        return null;
    }

    const ready = typeof why.text === 'string' && why.text.trim() !== '';
    const title = ready ? why.text : t('message.report_why_preparing');

    return (
        <HtmlTooltip
            title={<Box sx={{ p: '8px 10px', fontSize: '12px', lineHeight: '18px' }}>{title}</Box>}
            placement="left-start"
            arrow={false}
            certified={false}
        >
            <Box
                component="span"
                role="img"
                aria-label={ready ? t('message.report_why') : t('message.report_why_preparing')}
                data-test={`report-why-${elementId}`}
                data-state={ready ? 'ready' : 'preparing'}
                className="tile-element__info tile-element__info--why"
                onClick={(event) => event.stopPropagation()}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '16px',
                    lineHeight: 1,
                    cursor: 'pointer',
                    color: (theme) => alpha(theme.palette.text.primary, ready ? 0.64 : 0.32),
                }}
            >
                <IconMi icon="info-circle" />
            </Box>
        </HtmlTooltip>
    );
}
